import type { TemplateBase } from "@expert/expert-university";

import {
    getLearningBlockLabel,
    LEARNING_BLOCK_TITLES,
    UNASSIGNED_GROUP_KEY,
    UNASSIGNED_GROUP_LABEL,
} from "./learningBlocks";

export interface LearningBlockGroup {
    key: string;
    label: string;
    profiles: TemplateBase[];
    isUnassigned: boolean;
}

// Shared empty sentinel so every empty block returns the same reference (referential stability).
const EMPTY_PROFILES: TemplateBase[] = [];

export function groupProfilesByLearningBlock(profiles: TemplateBase[] | undefined): LearningBlockGroup[] {
    const buckets = new Map<number, TemplateBase[]>();
    const unassigned: TemplateBase[] = [];

    for (const profile of profiles ?? []) {
        // `learningBlockConfig.block` is the accordion assignment; fall back to the deprecated flat
        // `learningBlock` for profiles saved before the config field existed.
        const block = profile.learningBlockConfig?.block ?? profile.learningBlock;
        if (block !== undefined && block >= 1 && block <= LEARNING_BLOCK_TITLES.length) {
            const bucket = buckets.get(block);
            if (bucket) {
                bucket.push(profile);
            } else {
                buckets.set(block, [profile]);
            }
        } else {
            unassigned.push(profile);
        }
    }

    // Order each block's cards by `blockSequence` (ascending). Profiles without a sequence sort last;
    // the sort is stable, so their original input order is preserved among themselves.
    for (const bucket of buckets.values()) {
        bucket.sort(
            (a, b) =>
                (a.learningBlockConfig?.blockSequence ?? Number.POSITIVE_INFINITY) -
                (b.learningBlockConfig?.blockSequence ?? Number.POSITIVE_INFINITY),
        );
    }

    const groups: LearningBlockGroup[] = LEARNING_BLOCK_TITLES.map((_, index) => {
        const block = index + 1;
        return {
            key: String(block),
            label: getLearningBlockLabel(block),
            profiles: buckets.get(block) ?? EMPTY_PROFILES,
            isUnassigned: false,
        };
    });

    if (unassigned.length > 0) {
        groups.push({
            key: UNASSIGNED_GROUP_KEY,
            label: UNASSIGNED_GROUP_LABEL,
            profiles: unassigned,
            isUnassigned: true,
        });
    }

    return groups;
}
